import getConfig from "next/config";
import { bool, func, any } from "prop-types";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "@components/shared/Button";
import CircularLoader from "@components/shared/Icons/CircularLoader";
import Icon from "@components/shared/Icons/Icon";
import Typography from "components/shared/Typography";

import { useTranslation } from "@lib/i18n";

import { useFormatText } from "@hooks/useFormatText";

import { useVACPriceBreakdownSelector } from "@redux/reducers/deal";

import { COUNTRIES, IS_CLIENT } from "@shared/constants";
import { useIsVACJourneySelected } from "@hooks/useIsVACJourneySelected";
import { useGetPriceDetailProps } from "@hooks/useGetPriceDetailProps";

const { publicRuntimeConfig } = getConfig();
const { COUNTRY } = publicRuntimeConfig;

const LegalRequirementPopIn = () => {
  const { t } = useTranslation();

  const vacPriceBreakdown = useVACPriceBreakdownSelector();
  const isVACJourneySelected = useIsVACJourneySelected();

  const { isLeasingRequestLoading: isLoading } = useGetPriceDetailProps(false);

  const shouldShowLegalRequirements =
    isVACJourneySelected && COUNTRY === COUNTRIES.BE;

  const [isVisible, setIsVisible] = useState(shouldShowLegalRequirements);

  const [legalTextFields, setLegalTextFields] = useState({
    slogan: "",
    mainText: "",
    primaryText: "",
  });

  const setTextFields = priceObject => {
    if (!priceObject?.vehicleSimulation?.result?.disclaimer) {
      return;
    }

    const {
      disclaimer: { representativeExample, slogan },
      teaser: { primarytext },
    } = priceObject?.vehicleSimulation.result;

    setLegalTextFields({
      ...legalTextFields,
      slogan: slogan.text,
      mainText: representativeExample[0].text,
      primaryText: primarytext[0].text,
    });
  };

  const makeLegalText = text => useFormatText(text).join(" ");

  useEffect(() => {
    if (vacPriceBreakdown) {
      setTextFields(vacPriceBreakdown);
    }
  }, [
    vacPriceBreakdown,
    vacPriceBreakdown?.vehicleSimulation,
    vacPriceBreakdown?.vehicleSimulation?.result,
  ]);

  if (!IS_CLIENT || !shouldShowLegalRequirements || !isVisible) {
    return null;
  }

  return createPortal(
    <div
      className="
      flex flex-col items-center gap-4
      fixed bottom-0 left-0 right-0 z-50
      w-full p-4 overflow-y-auto
      bg-white/90 backdrop-blur-lg border-t-2 border-primaryOrange shadow--md
      max-h-[100svh]
      smallMobile:max-h-[25svh]
      sm:max-h-[50svh]
    "
    >
      {isLoading ? (
        <div className="grid place-items-center p-8">
          <Icon
            className="size-12 text-primaryDark"
            icon={<CircularLoader />}
          />
        </div>
      ) : (
        <>
          <h1
            className="text-2xl text-center uppercase font-normal"
            dangerouslySetInnerHTML={{
              __html: makeLegalText(legalTextFields.slogan),
            }}
          />
          <Typography>{legalTextFields.mainText}</Typography>
          <Button
            className="shrink-0"
            data-id="seller-contact-close-icon"
            onClick={() => setIsVisible(false)}
          >
            {t("basket.financing.ctaButton")}
          </Button>
        </>
      )}
    </div>,
    document?.body
  );
};

LegalRequirementPopIn.propTypes = {
  isLoading: any.isRequired,
  isOpen: bool.isRequired,
  onClose: func.isRequired,
};

export default LegalRequirementPopIn;
